import React, { useEffect, useRef, useState } from 'react'
import {
  Button,
  Cell,
  DialogOverlay,
  DialogOverlayActions,
  DialogOverlayFooter,
  Grid,
  IconButton,
  InputGroup,
  LabelTooltip,
  Row,
  TextField,
  useOverlay,
  DangerButton,
} from '@fs/zion-ui'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { ContentDelete } from '@fs/zion-icon'
import PropTypes from 'prop-types'
import { NoticeDoNot } from '@fs/zion-icon/dist/cjs/icons'
import { deepCopy } from '../../../util/Utils'
import { marginBottom, padding } from '../../../util/CssUtil'
import initDateStandards from '../DmcDateStandards'
import { initPlaceStandards } from '../DmcPlaceStandards'
import { equalsTreatEmptyNullUndefinedTheSame, getLastPathElement } from '../../../util/StringUtil'
import { RawData } from '../RawData'
import { makeNewDateConclusion, makeNewPlaceConclusion } from '../../person/PersonUtils'
import { DmcConclusionFactUIOnly } from './DmcConclusionFactUIOnly'

export default () => {}

/* eslint-disable react-hooks/exhaustive-deps */

const deleteDialog = [
  padding('10px'),
  marginBottom('10px'),
  css`
  border: 1px #9e9e9e solid
  border-radius: 5px
              `,
]

export function DmcConclusionFact({
  creating,
  editable,
  noDelete,
  factConclusion,
  factOptions,
  associationOptions,
  onSaveCallback,
  onDeleteCallback,
  onCancelCallback,
  withChangeMessage,
  withDeleteConfirmation,
  withInlineEditButton,
}) {
  const { t } = useTranslation()
  const { factState, factActions } = useUpdateConclusionFact(
    factConclusion,
    factOptions,
    associationOptions,
    onSaveCallback,
    onDeleteCallback,
    onCancelCallback
  )
  const testType = factState.factType.split('/').pop().toLowerCase()
  editable = editable == null ? true : editable
  withChangeMessage = withChangeMessage == null ? true : withChangeMessage
  withDeleteConfirmation = withDeleteConfirmation == null ? true : withDeleteConfirmation
  withInlineEditButton = withInlineEditButton == null ? true : withInlineEditButton

  useEffect(() => {
    factActions.checkIfHasChanged()
  }, [
    factState.dateStandards?.original,
    factState.dateStandards?.primaryText,
    factState.placeStandards?.originalText,
    factState.placeStandards?.standard?.standardizedText,
    factState.modifiableFact?.value?.type,
    factState.modifiableFact?.value?.associatedParent,
    factState.modifiableFact?.attribution?.changeMessage,
    factState.modifiableFact?.value?.value,
  ])

  if (!(factState && factConclusion)) {
    return null
  }

  return (
    <Grid>
      {/* Row */}
      <Cell columns={1}>
        <InputGroup label={'\xa0'}>
          {creating && (
            <IconButton
              dmcflag="deleteNewFact"
              Icon={ContentDelete}
              onClick={factActions.onDelete}
              emphasis="high"
              color="red"
              id={`${testType}NewFactDelete`}
              description={t('fact.deleteNew', 'Delete New Fact')}
            />
          )}
          {!creating && (
            <>
              {noDelete ? (
                <LabelTooltip
                  target={
                    <NoticeDoNot data-testid="deathFactCannotBeDeletedIndicator" background="orange" color="white" />
                  }
                  label={t('deleteFactNotAllowedMessage', 'Not allowed to delete death fact on deceased person')}
                />
              ) : (
                <IconButton
                  id={`${testType}FactDelete`}
                  Icon={ContentDelete}
                  onClick={() => {
                    if (withDeleteConfirmation) {
                      factActions.deleteDialogOpener.open()
                    } else {
                      factActions.onDelete()
                    }
                  }}
                  emphasis="high"
                  color="red"
                  description={t('fact.delete', 'Delete Fact')}
                />
              )}
            </>
          )}
        </InputGroup>
      </Cell>
      <Cell columns={8}>
        <DmcConclusionFactUIOnly
          editable={editable}
          type={factState.factType}
          setType={factActions.setBothFactTypeAndModifiableFact}
          typeOptions={factState.factOptions}
          associationType={factState.associationType}
          setAssociation={factActions.setBothAssociationTypeAndModifiableFact}
          associationOptions={factState.associationOptions}
          date={factState.dateStandards}
          setDate={factActions.setBothDateStandardsAndModifiableFact}
          place={factState.placeStandards}
          setPlace={factActions.setBothPlaceStandardsAndModifiableFact}
          value={factState.value}
          setValue={factActions.setBothValueAndModifiableFact}
        />
      </Cell>
      {/* row */}
      {withChangeMessage && (
        <Cell columns={11}>
          <TextField
            id={`${testType}FactChangeReason`}
            label={t('reason', 'Reason')}
            value={factState.changeMessage}
            onChange={(e) => factActions.setBothChangeMessageAndModifiableFact(e.target.value)}
          />
        </Cell>
      )}
      <Cell columns={3}>
        {withInlineEditButton && !editable && !factState.singleEditable && (
          <InputGroup label={'\xa0'}>
            <Row>
              <Button id={`${testType}FactCreate`} onClick={() => factActions.setSingleEditable(true)}>
                {t('action.edit', 'Edit')}
              </Button>
            </Row>
          </InputGroup>
        )}
        {(factState.changed || factState.singleEditable) && (
          <InputGroup label={'\xa0'}>
            <Row>
              <Button emphasis="high" id={`${testType}FactUpdate`} onClick={factActions.onSave}>
                {creating ? t('action.create', 'Create') : t('action.update', 'Update')}
              </Button>
              <Button emphasis="medium" role="button" id={`${testType}FactCancel`} onClick={factActions.onCancel}>
                {t('action.cancel', 'Cancel')}
              </Button>
            </Row>
          </InputGroup>
        )}
      </Cell>
      {/* Row */}
      <Cell columns={12}>
        <RawData state={factState} testId="dmcConclusionFactState" />
      </Cell>
      {/* Row */}
      <Cell columns={12}>
        <DeleteConfirmationPopup factState={factState} factActions={factActions} />
      </Cell>
    </Grid>
  )
}

DmcConclusionFact.prototype = {
  // eslint-disable-next-line react/forbid-prop-types
  factConclusion: PropTypes.object,
  factOptions: PropTypes.arrayOf({
    label: PropTypes.string,
    value: PropTypes.string,
  }),
  associationOptions: PropTypes.arrayOf({
    label: PropTypes.string,
    value: PropTypes.string,
  }),
  onSaveCallback: PropTypes.func,
  onDeleteCallback: PropTypes.func,
  onCancelCallback: PropTypes.func,
  withChangeMessage: PropTypes.bool,
  withDeleteConfirmation: PropTypes.bool,
  creating: PropTypes.bool,
}

/**
 * Hook - Controller!
 */

function DeleteConfirmationPopup({ factState, factActions }) {
  const { t } = useTranslation()
  const testType = factState.factType.split('/').pop().toLowerCase()
  const focusElementRef = useRef()

  return (
    <DialogOverlay
      {...factActions.deleteDialogOpener}
      title={t('conclusionFact.deleteFact', 'Delete Fact')}
      focusRef={focusElementRef}
    >
      <div css={deleteDialog}>
        <strong>{factActions.getReadableFactType()}</strong>
        <div>{factState.dateStandards?.originalText}</div>
        <div>{factState.placeStandards?.originalText}</div>
      </div>
      <div>{t('confirm.deleteThisFact', 'Are you sure you want to delete this fact?')}</div>
      <TextField
        ref={focusElementRef}
        id={`${testType}FactDeletionReason`}
        label={t('reason', 'Reason')}
        value={factState.changeMessage}
        onChange={(event) => factActions.setChangeMessage(event.target.value)}
      />

      <DialogOverlayFooter>
        <DialogOverlayActions>
          <DangerButton id={`${testType}FactDeletionReasonButton`} onClick={() => factActions.onDelete()}>
            {t('action.delete', 'Delete')}
          </DangerButton>
          <Button
            id={`${testType}FactDeletionReasonCancelButton`}
            onClick={() => factActions.onCancel()}
            emphasis="medium"
          >
            {t('action.cancel', 'Cancel')}
          </Button>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}

function useUpdateConclusionFact(
  factConclusion,
  _factOptions_,
  _associationOptions_,
  onSaveCallback,
  onDeleteCallback,
  onCancelCallback
) {
  const { t } = useTranslation()
  // Initial Fact State
  const initialOriginalDate = deepCopy(factConclusion?.value?.date?.original)
  const initialStandardDate = deepCopy(factConclusion?.value?.date?.normalized?.[0]?.value)
  const initialOriginalPlace = deepCopy(factConclusion?.value?.place?.original)
  const initialStandardPlace = deepCopy(factConclusion?.value?.place?.names?.[0]?.value)
  const initialStandardPlaceId = deepCopy(factConclusion?.value?.place?.id)
  const initialConclusionType = deepCopy(factConclusion.value.type)
  const initialAssociatedParent = deepCopy(factConclusion.value?.associatedParent)
  const initialChangeMessage = deepCopy(factConclusion?.attribution?.changeMessage)
  const initialValue = deepCopy(factConclusion.value.value)

  // useState stuff for Rendering
  const [modifiableFact, setModifiableFact] = useState(deepCopy(factConclusion))
  const [factOptions, setFactOptions] = useState(_factOptions_ || getStandardOptions(t))
  const [associationOptions, setAssociationOptions] = useState(_associationOptions_)
  const [dateStandards, setDateStandards] = useState(initDateStandards(factConclusion?.value?.date))
  const [placeStandards, setPlaceStandards] = useState(
    initPlaceStandards(initialOriginalPlace, initialStandardPlace, initialStandardPlaceId)
  )
  const [value, setValue] = useState(factConclusion.value.value)
  const [factType, setFactType] = useState(initialConclusionType)
  const [associationType, setAssociationType] = useState(initialAssociatedParent)
  const [changed, setChanged] = useState(false)
  const [changeMessage, setChangeMessage] = useState(initialChangeMessage)
  const [singleEditable, setSingleEditable] = useState(false)

  // Putting state together makes testing easier
  const factState = {
    modifiableFact,
    factOptions,
    associationOptions,
    dateStandards,
    placeStandards,
    value,
    factType,
    associationType,
    changed,
    changeMessage,
    singleEditable,
  }

  // Putting functions together makes testing easier because I can mock them
  const factActions = {
    setModifiableFact,
    setFactOptions,
    setAssociationOptions,
    setDateStandards,
    setPlaceStandards,
    setFactType,
    setAssociationType,
    setValue,
    setChanged,
    setChangeMessage,
    setSingleEditable,
    deleteDialogOpener: useOverlay(),
    setBothFactTypeAndModifiableFact: (type) => {
      modifiableFact.value.type = type
      factActions.setModifiableFact(modifiableFact)
      factActions.setFactType(type)
    },
    setBothAssociationTypeAndModifiableFact: (newAssociatedParent) => {
      modifiableFact.value.associatedParent = newAssociatedParent
      factActions.setModifiableFact(modifiableFact)
      factActions.setAssociationType(newAssociatedParent)
    },
    setBothDateStandardsAndModifiableFact: (newDateStandards) => {
      factActions.setDateStandards(newDateStandards)
      modifiableFact.value.date = makeNewDateConclusion(newDateStandards?.original, newDateStandards?.primaryText)
      factActions.setModifiableFact(modifiableFact)
    },
    setBothPlaceStandardsAndModifiableFact: (newPlaceStandards) => {
      factActions.setPlaceStandards(newPlaceStandards)
      modifiableFact.value.place = makeNewPlaceConclusion(
        newPlaceStandards.originalText,
        newPlaceStandards?.standard?.standardizedText,
        newPlaceStandards?.standard?.id
      )
      factActions.setModifiableFact(modifiableFact)
    },
    setBothChangeMessageAndModifiableFact: (newChangeMessage) => {
      factActions.setChangeMessage(newChangeMessage)
      modifiableFact.attribution && (modifiableFact.attribution.changeMessage = newChangeMessage)
      factActions.setModifiableFact(modifiableFact)
    },
    setBothValueAndModifiableFact: (newValue) => {
      modifiableFact.value.value = newValue
      factActions.setModifiableFact(modifiableFact)
      factActions.setValue(newValue)
    },
    getReadableFactType: () => {
      return t(`fact.type.${modifiableFact.value.type.toLowerCase()}`, getLastPathElement(modifiableFact.value.type))
    },
    reset: () => {
      factActions.setModifiableFact(deepCopy(factConclusion))
      factActions.setDateStandards(initDateStandards(factConclusion?.value?.date))
      factActions.setPlaceStandards(
        initPlaceStandards(initialOriginalPlace, initialStandardPlace, initialStandardPlaceId)
      )
      factActions.setFactType(initialConclusionType)
      factActions.setValue(initialValue)
      factActions.setChanged(false)
      factActions.setChangeMessage(initialChangeMessage)
    },
    onSave: () => {
      onSaveCallback?.(modifiableFact, changeMessage)
      factActions.setChanged(false)
      factActions.setSingleEditable(false)
    },
    onDelete: () => {
      onDeleteCallback?.(modifiableFact, changeMessage)
      factActions.deleteDialogOpener.close()
    },
    onCancel: () => {
      onCancelCallback?.(deepCopy(factConclusion))
      factActions.reset()
      factActions.deleteDialogOpener.close()
      factActions.setSingleEditable(false)
    },
    checkIfHasChanged: () => {
      const hasChanged =
        !equalsTreatEmptyNullUndefinedTheSame(dateStandards?.original, initialOriginalDate) ||
        !equalsTreatEmptyNullUndefinedTheSame(dateStandards?.primaryText, initialStandardDate) ||
        !equalsTreatEmptyNullUndefinedTheSame(placeStandards?.originalText, initialOriginalPlace) ||
        !equalsTreatEmptyNullUndefinedTheSame(placeStandards?.standard?.standardizedText, initialStandardPlace) ||
        !equalsTreatEmptyNullUndefinedTheSame(modifiableFact.value.type, initialConclusionType) ||
        !equalsTreatEmptyNullUndefinedTheSame(modifiableFact.value?.associatedParent, initialAssociatedParent) ||
        !equalsTreatEmptyNullUndefinedTheSame(modifiableFact.attribution?.changeMessage, initialChangeMessage) ||
        !equalsTreatEmptyNullUndefinedTheSame(modifiableFact.value.value, initialValue)
      factActions.setChanged(hasChanged)
    },
  }
  return { factState, factActions }
}

/**
 * Helper Functions
 */

function getStandardOptions(t) {
  return [
    {
      label: t('birth', 'Birth'),
      value: 'http://gedcomx.org/Birth',
    },
    {
      label: t('burial', 'Burial'),
      value: 'http://gedcomx.org/Burial',
    },
    {
      label: t('christening', 'Christening'),
      value: 'http://gedcomx.org/Christening',
    },
    {
      label: t('death', 'Death'),
      value: 'http://gedcomx.org/Death',
    },
    {
      label: t('marriage', 'Marriage'),
      value: 'http://gedcomx.org/Marriage',
    },
  ]
}

/* eslint-enable react-hooks/exhaustive-deps */
